import React, { useEffect, useMemo } from 'react';
import { DialogContent } from '@mui/material';
import { useAsperaUpload } from '../../../../src/utils/hooks/object-browser/useAsperaUpload';
import { useCreateFolder } from '../../../../src/utils/hooks/object-browser/useCreateFolder';
import { AutoButtonGroup } from '../../../../src/components/library/AutoButtonGroup';
import Dialog from '../../../../src/components/library/Dialog';
import DialogTitle, { CloseButton } from '../../../../src/components/library/DialogTitle';
import { Button } from '../../../../src/components/library';
import { CreateFolderDialog } from './CreateFolderDialog';

interface AsperaUploadButtonProps {
	currentBucketPath: string;
	bucketInfo: {
		id: string;
		isManaged: boolean;
	};
}

interface Installer {
	platform: string;
	type: string;
	url: string;
}

interface InstallerViewProps {
	installers: Installer[];
	onLaunch: () => void;
	onInstall: (url: string) => void;
}

function InstallerView({ installers, onLaunch, onInstall }: InstallerViewProps) {
	return (
		<div style={{ padding: 24 }}>
			<div style={{ display: 'flex', gap: 16, marginBottom: 16, width: '100%' }}>
				<Button type="button" onClick={onLaunch} style={{ flex: 1 }}>
					Launch Aspera Connect
				</Button>
				{installers.map((installer: Installer) => (
					<Button
						key={installer.platform + installer.type}
						type="button"
						onClick={() => onInstall(installer.url)}
						style={{ flex: 1 }}
					>
						Install ({installer.platform} - {installer.type})
					</Button>
				))}
			</div>
			{installers.length === 0 && <div>No installers found for your platform.</div>}
		</div>
	);
}

export function AsperaUploadButton({ currentBucketPath, bucketInfo }: AsperaUploadButtonProps) {
	const {
		transferId,
		transferStatus,
		isUploading,
		showFallback,
		installers,
		handleCloseFallback,
		handleUpload,
		getTransferStatus,
		handleLaunch,
	} = useAsperaUpload({ currentBucketPath, bucketInfo });

	const {
		handleCreateFolder,
		isCreating,
		showDialog: showCreateFolderDialog,
		handleCloseDialog: handleCloseCreateFolderDialog,
		handleOpenDialog: handleOpenCreateFolderDialog,
		folderName,
		validationError,
		handleFolderNameChange,
	} = useCreateFolder({ currentBucketPath, bucketInfo });

	useEffect(() => {
		if (!transferId) return;
		const interval = setInterval(() => {
			getTransferStatus();
		}, 2000);

		// Stop polling if transferStatus is 'completed'
		if (transferStatus && transferStatus.status === 'completed') {
			clearInterval(interval);
		}

		function cleanup() {
			clearInterval(interval);
		}
		// eslint-disable-next-line consistent-return
		return cleanup;
	}, [transferId, getTransferStatus, transferStatus]);

	const handleClick = (id: string) => {
		if (id === 'file') {
			handleUpload(false);
		} else if (id === 'folder') {
			handleUpload(true);
		} else if (id === 'craete-folder') {
			handleOpenCreateFolderDialog();
		}
	};

	const openWithOptions = useMemo(
		() => [
			{ id: 'file', label: '+ Upload Files' },
			{ id: 'folder', label: '+ Upload Folders', default: true },
			{ id: 'craete-folder', label: '+ Create Folder', default: false },
		],
		[],
	);

	return (
		<>
			<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, minWidth: '166px' }}>
				<AutoButtonGroup
					nxstyle="secondary-orange"
					storeKey="export-format"
					height={32}
					options={openWithOptions}
					onOptionClick={handleClick}
					disabled={isUploading || isCreating}
					expandButtonAriaLabel="Expand Upload Menu"
				/>
			</div>
			<Dialog open={showFallback} onClose={handleCloseFallback} fullWidth>
				<DialogTitle>
					IBM Aspera Installer
					<CloseButton onClose={handleCloseFallback} />
				</DialogTitle>
				<DialogContent>
					<InstallerView
						installers={installers}
						onLaunch={handleLaunch}
						onInstall={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
					/>
				</DialogContent>
			</Dialog>
			<CreateFolderDialog
				open={showCreateFolderDialog}
				onClose={handleCloseCreateFolderDialog}
				folderName={folderName}
				validationError={validationError}
				onFolderNameChange={handleFolderNameChange}
				onCreateFolder={handleCreateFolder}
				isCreating={isCreating}
			/>
		</>
	);
}
